import profilesAtom from "recoil/account/profiles";
import phoneDetailsAtom from "recoil/account/phoneDetails";
import paymentInfoAtom from "recoil/account/paymentInfo";
import bankDetailsAtom from "recoil/account/bankDetails";
import billingInfoAtom from "recoil/account/billingInfo";
import promoteChannelsAtom from "recoil/account/promoteChannels";
import rhpLinkAccountAtom from "recoil/account/rhpLinkAccount";
import authAtom from "recoil/account/auth";
import dateJoinedAtom from "recoil/account/dateJoined";
import heardFromAtom from "../recoil/account/heardFrom";
import accService from "services/account";
import apiClient from "services/apiClient";
import { useRecoilState } from "recoil";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import useToast from "./useToast";
import useRHPEvents, { RHP_EVENTS_KEY } from "./useRHPEvents";
import useRHPGroups, { RHP_GROUPS_KEY } from "./useRHPGroups";
import { ACCOUNT_KEY } from "./useAccountData";

const FIFTEEN_MINUTES = 15 * 60 * 1000;

const useAccount = () => {
    const toast = useToast();
    const service = accService();
    const queryClient = useQueryClient();

    const [, setAuth] = useRecoilState(authAtom);
    const [, setDateJoined] = useRecoilState(dateJoinedAtom);
    const [heardFrom, setHeardFrom] = useRecoilState(heardFromAtom);
    const [profiles, setProfiles] = useRecoilState(profilesAtom);
    const [phones, setPhones] = useRecoilState(phoneDetailsAtom);
    const [paymentInfo, setPaymentInfo] = useRecoilState(paymentInfoAtom);
    const [bankDetails, setBankDetails] = useRecoilState(bankDetailsAtom);
    const [billingInfo, setBillingInfo] = useRecoilState(billingInfoAtom);
    const [rhpLinkAcc, setRhpLinkAcc] = useRecoilState(rhpLinkAccountAtom);
    //eslint-disable-next-line no-unused-vars
    const [promoteChannels, setPromoteChannels] = useRecoilState(promoteChannelsAtom);

    const { data: rhpEvents = [] } = useRHPEvents();
    const { data: rhpGroups = [] } = useRHPGroups();

    // ── Mutations ─────────────────────────────────────────────────────────────

    const createAccount = useMutation(
        (data) => service.createAccount(data).then(r => r.data),
        {
            onSuccess: (data) => {
                const {
                    DateJoined, ProfileDetails, PhoneDetails,
                    PaymentInfoDetails, BillingInfoDetails, PromoteChannels, RHPLinkedAccount,
                    HeardFrom
                } = data;
                const { BankDetails, AffiliatePaymentTypeID, Paypal, Wise, PaymentID, ProgramID } = PaymentInfoDetails;
                setDateJoined(DateJoined);
                setHeardFrom(HeardFrom);
                setProfiles(ProfileDetails);
                setPhones(PhoneDetails);
                setPromoteChannels(PromoteChannels);
                setBillingInfo(BillingInfoDetails);
                setBankDetails(BankDetails);
                setPaymentInfo(prev => ({ ...prev, AffiliatePaymentTypeID, Paypal, Wise, PaymentID, ProgramID }));
                setRhpLinkAcc(RHPLinkedAccount);
                setAuth(prev => ({ ...prev, accountLoaded: true }));
            },
            onError: (error) => toast.error("Failed create account", error),
        }
    );

    const updateProfileDetails = useMutation(
        (data) => service.updateProfileDetails(data).then(r => r.data),
        {
            onSuccess: ({ ProfileDetails, PromoteChannels }) => {
                setProfiles(ProfileDetails);
                setPromoteChannels(PromoteChannels);
            },
            onError: (error) => toast.error("Failed updating profile details", error),
        }
    );

    const updatePaymentInfo = useMutation(
        (data) => service.updatePaymentInfoDetails(data).then(r => r.data),
        {
            onSuccess: ({ BankDetails, AffiliatePaymentTypeID, Paypal, Wise, PaymentID, ProgramID }) => {
                setBankDetails(BankDetails);
                setPaymentInfo(prev => ({ ...prev, AffiliatePaymentTypeID, Paypal, Wise, PaymentID, ProgramID }));
            },
            onError: (error) => toast.error("Failed updating payment info", error),
        }
    );

    const updateBillingInfo = useMutation(
        (data) => service.updateBillingInfoDetails(data).then(r => r.data),
        {
            onSuccess: (data) => setBillingInfo(data),
            onError: (error) => toast.error("Failed updating billing info", error),
        }
    );

    const updatePhoneDetails = useMutation(
        (data) => service.updatePhone(data).then(r => r.data),
        {
            onSuccess: (data) => setPhones(data),
            onError: (error) => toast.error("Failed updating phone number", error),
        }
    );

    const validatePhone = useMutation(
        (code) => service.validatePhone({ VerificationCode: code }).then(r => r.data),
        {
            onSuccess: (data) => setPhones(data),
            onError: (error) => toast.error("Failed verifying code", error),
        }
    );

    const verifyPhone = useMutation(
        (data) => service.verfifyPhoneValidationCode(data).then(r => r.data),
        {
            onError: (error) => toast.error("Failed verifying code", error),
        }
    );

    const requestPhoneCode = useMutation(
        (data) => service.requestValidationCode(data),
        {
            onError: (error) => toast.error("Failed requesting sms code", error),
        }
    );

    const updatePassword = useMutation(
        (data) => service.updatePassword(data),
        {
            onError: (error) => {
                if (String(error).indexOf("401") !== -1) {
                    toast.error(String(error));
                } else {
                    toast.error("Failed updating password", error);
                }
            },
        }
    );

    const linkToRHPAccount = useMutation(
        (data) => service.linkToRHPAccount(data).then(r => r.data),
        {
            onSuccess: ({ RHPLinkedAccount, Events, Groups }) => {
                setRhpLinkAcc(RHPLinkedAccount);
                queryClient.setQueryData(RHP_EVENTS_KEY, Events);
                queryClient.setQueryData(RHP_GROUPS_KEY, Groups);
            },
            onError: (error) => toast.error("Failed linking to rhp", error),
        }
    );

    const unLinkFromRHPAccount = useMutation(
        (data) => service.unLinkFromRHPAccount(data).then(r => r.data),
        {
            onSuccess: ({ RHPLinkedAccount, Events, Groups }) => {
                setRhpLinkAcc(RHPLinkedAccount);
                queryClient.setQueryData(RHP_EVENTS_KEY, Events);
                queryClient.setQueryData(RHP_GROUPS_KEY, Groups);
            },
            onError: (error) => toast.error("Failed unlinking to rhp", error),
        }
    );

    // ── Imperative fetch (backed by TQ cache for deduplication) ───────────────

    const getAccount = async () => {
        return queryClient.fetchQuery(
            ACCOUNT_KEY,
            () => apiClient.get('/Account/').then(r => r.data),
            { staleTime: FIFTEEN_MINUTES }
        )
        .then(data => {
            const {
                DateJoined, ProfileDetails, PhoneDetails,
                PaymentInfoDetails, BillingInfoDetails, PromoteChannels,
                RHPLinkedAccount, Events, Groups, HeardFrom
            } = data;
            const { BankDetails, AffiliatePaymentTypeID, Paypal, Wise, PaymentID, ProgramID } = PaymentInfoDetails;

            setProfiles(ProfileDetails);
            setPhones(PhoneDetails);
            setPromoteChannels(PromoteChannels);
            setBillingInfo(BillingInfoDetails);
            setBankDetails(BankDetails);
            setPaymentInfo(prev => ({ ...prev, AffiliatePaymentTypeID, Paypal, Wise, PaymentID, ProgramID }));
            setRhpLinkAcc(RHPLinkedAccount);
            setDateJoined(DateJoined);
            setHeardFrom(HeardFrom);
            setAuth(prev => ({ ...prev, accountLoaded: true }));

            queryClient.setQueryData(RHP_EVENTS_KEY, Events);
            queryClient.setQueryData(RHP_GROUPS_KEY, Groups);
        })
        .catch(error => {
            toast.error("Failed retrieving account", error);
            throw error;
        });
    };

    return {
        // data
        heardFrom,
        paymentInfo,
        bankDetails,
        profiles,
        phoneDetail: phones,
        billingInfo,
        promoteChannels,
        rhpLinkAcc,
        rhpEvents,
        rhpGroups,
        // mutations
        createAccount,
        validatePhone,
        verifyPhone,
        requestPhoneCode,
        updatePaymentInfo,
        updateBillingInfo,
        updateProfileDetails,
        updatePhoneDetails,
        updatePassword,
        linkToRHPAccount,
        unLinkFromRHPAccount,
        // imperative loader
        getAccount,
    };
};

export default useAccount;
